import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [utilisateur, setUtilisateur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modeVue, setModeVue] = useState('gestion');
  const [espaceActif, setEspaceActif] = useState('projet'); // 'projet' ou 'ferme'
  // Bascule dédiée à la capacité "Aperçu ferme" — jamais mélangée au menu
  // habituel de la personne, un vrai espace à part qu'on quitte/rejoint
  // explicitement, comme le switch Gestion/Investisseur.
  const [vueApercuFerme, setVueApercuFermeState] = useState(false);
  // Même principe pour la capacité composable "est_investisseur" (greffée
  // sur un rôle qui n'est PAS déjà investisseur/tech_invest/gestion_invest,
  // ex: un stagiaire ou un technicien qui a aussi investi) — un espace à
  // part avec son propre switch, jamais mélangé au modeVue de tech_invest/
  // gestion_invest dont la mécanique reste intacte.
  const [vueInvestisseurCapacite, setVueInvestisseurCapaciteState] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('utilisateur');
    const savedMode = localStorage.getItem('modeVue');
    const savedEspace = localStorage.getItem('espaceActif');
    const savedVueApercuFerme = localStorage.getItem('vueApercuFerme');
    const savedVueInvestisseurCapacite = localStorage.getItem('vueInvestisseurCapacite');

    if (token && userData) {
      const user = JSON.parse(userData);
      setUtilisateur(user);
      if (user.role === 'tech_invest') {
        setModeVue(savedMode || 'technicien');
      } else if (user.role === 'gestion_invest') {
        setModeVue(savedMode || 'gestion');
      }
      setEspaceActif(savedEspace || 'projet');
      setVueApercuFermeState(savedVueApercuFerme === '1');
      setVueInvestisseurCapaciteState(savedVueInvestisseurCapacite === '1');

      // Rafraîchit en arrière-plan depuis le serveur — sans ça, un droit
      // accordé après coup (ex: capacité "Aperçu ferme") ne serait visible
      // qu'après une déconnexion/reconnexion complète.
      authService.moi()
        .then(res => {
          const utilisateurFrais = { ...user, ...res.data };
          localStorage.setItem('utilisateur', JSON.stringify(utilisateurFrais));
          setUtilisateur(utilisateurFrais);
        })
        .catch(() => {});
    }
    setLoading(false);
  }, []);

  const login = async (email, mot_de_passe) => {
    const response = await authService.login({ email, mot_de_passe });
    const { token, utilisateur } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('utilisateur', JSON.stringify(utilisateur));
    setUtilisateur(utilisateur);
    if (utilisateur.role === 'tech_invest') {
      setModeVue('technicien');
    } else if (utilisateur.role === 'gestion_invest') {
      setModeVue('gestion');
    }
    setEspaceActif('projet');
    return utilisateur;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('utilisateur');
    localStorage.removeItem('modeVue');
    localStorage.removeItem('espaceActif');
    localStorage.removeItem('projet_actif_id');
    localStorage.removeItem('vueApercuFerme');
    localStorage.removeItem('vueInvestisseurCapacite');
    setUtilisateur(null);
  };

  const switchMode = (mode) => {
    setModeVue(mode);
    localStorage.setItem('modeVue', mode);
  };

  const switchEspace = (espace) => {
    setEspaceActif(espace);
    localStorage.setItem('espaceActif', espace);
  };

  const setVueApercuFerme = (valeur) => {
    setVueApercuFermeState(valeur);
    localStorage.setItem('vueApercuFerme', valeur ? '1' : '0');
  };

  const setVueInvestisseurCapacite = (valeur) => {
    setVueInvestisseurCapaciteState(valeur);
    localStorage.setItem('vueInvestisseurCapacite', valeur ? '1' : '0');
  };

  // Met à jour l'utilisateur en mémoire ET dans le localStorage — utile
  // après un changement qui doit être visible immédiatement sans recharger
  // la page (ex: fin de l'onboarding stagiaire, qui doit débloquer
  // ProtectedRoute tout de suite).
  const mettreAJourUtilisateur = (champs) => {
    setUtilisateur((prev) => {
      const maj = { ...prev, ...champs };
      localStorage.setItem('utilisateur', JSON.stringify(maj));
      return maj;
    });
  };

  return (
    <AuthContext.Provider value={{ utilisateur, login, logout, loading, modeVue, switchMode, espaceActif, switchEspace, vueApercuFerme, setVueApercuFerme, vueInvestisseurCapacite, setVueInvestisseurCapacite, mettreAJourUtilisateur }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);